import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { Decimal } from 'decimal.js';
import { isTaxMarked } from '~/models/bundle-tax';
import { isClientTaxExempt } from '~/models/ClientModel';
export const savedAgreementTax = (tax)=>tax == null || tax.type === 'none' ? null : tax;
export const agreementTaxRate = (tax)=>(tax === null || tax === void 0 ? void 0 : tax.type) === 'taxable' ? tax.taxable : null;
export const isAgreementCollectingTax = (tax)=>(tax === null || tax === void 0 ? void 0 : tax.type) === 'taxable';
export const taxPresetFromRateDTO = (rate)=>_object_spread({
        id: rate.id,
        name: rate.name,
        status: 'active',
        combinedRate: rate.combinedRate,
        components: rate.components.map((component)=>_object_spread({
                id: component.id,
                name: component.name,
                rate: component.rate
            }, component.integrations != null ? {
                integrations: component.integrations
            } : {}))
    }, rate.combinedRate != null ? {
        combinedRate: rate.combinedRate
    } : {}, rate.integrations != null ? {
        integrations: rate.integrations
    } : {});
export const salesTaxConfigFromDTO = (dto)=>({
        enabled: dto.enabled,
        displayMode: dto.displayMode,
        label: dto.label,
        presets: dto.rates.map(taxPresetFromRateDTO)
    });
export const rateWriteBody = (preset, components)=>({
        name: preset.name,
        components
    });
const normalizedName = (name)=>name.trim().toLowerCase();
const planComponentEntry = (component, pool)=>{
    const pooled = pool.find((candidate)=>normalizedName(candidate.name) === normalizedName(component.name));
    if (pooled == null) {
        return {
            type: 'create',
            create: {
                name: component.name,
                rate: component.rate
            }
        };
    }
    return pooled.rate === component.rate ? {
        type: 'reference',
        reference: {
            id: pooled.id
        }
    } : {
        type: 'update',
        update: {
            id: pooled.id,
            name: pooled.name,
            rate: component.rate
        }
    };
};
export const planComponentEntries = (param)=>{
    let { components, pool } = param;
    return components.map((component)=>planComponentEntry(component, pool));
};
export const hasPoolWrite = (entries)=>entries.some((entry)=>entry.type !== 'reference');
export const componentSuggestionsFromPool = (pool)=>pool.map((component)=>({
            name: component.name,
            rate: component.rate,
            subLabel: formatPercent(component.rate)
        }));
export const formatPercent = (rate)=>{
    const n = typeof rate === 'string' ? parseFloat(rate) : rate;
    if (Number.isNaN(n)) return '0.00%';
    return "".concat(n.toFixed(2), "%");
};
export const addPreset = (config, next)=>_object_spread_props(_object_spread({}, config), {
        presets: [
            ...config.presets,
            next
        ]
    });
export const isDuplicateName = (presets, name, excludingId)=>presets.some((p)=>p.id !== excludingId && p.name.trim().toLowerCase() === name.trim().toLowerCase());
export const integrationMappingFor = (integrations, integrationName)=>integrations === null || integrations === void 0 ? void 0 : integrations.find((mapping)=>mapping.type === integrationName);
export const isComponentMapped = (component)=>{
    var _ref;
    var _component_integrations;
    return (_ref = (_component_integrations = component.integrations) === null || _component_integrations === void 0 ? void 0 : _component_integrations.some((mapping)=>mapping.value.status !== 'unmapped')) !== null && _ref !== void 0 ? _ref : false;
};
export const presetMappingStatusFor = (preset, integrationName)=>{
    var _ref;
    var _integrationMappingFor;
    return (_ref = (_integrationMappingFor = integrationMappingFor(preset.integrations, integrationName)) === null || _integrationMappingFor === void 0 ? void 0 : _integrationMappingFor.value.status) !== null && _ref !== void 0 ? _ref : 'unmapped';
};
export const needsTaxRate = (param)=>{
    let { presetId, lines, clientTaxSettings, config } = param;
    if ((config === null || config === void 0 ? void 0 : config.enabled) !== true) return false;
    if (clientTaxSettings != null && isClientTaxExempt(clientTaxSettings)) return false;
    if (!lines.some((line)=>line.taxable)) return false;
    return presetId == null;
};
export function isServiceTaxable(service) {
    var _service_taxable, _ref;
    var _service_bundle;
    return isTaxMarked({
        ownMark: (_service_taxable = service.taxable) !== null && _service_taxable !== void 0 ? _service_taxable : false,
        childMarks: (_ref = (_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services.map((included)=>{
            var _included_taxable;
            return (_included_taxable = included.taxable) !== null && _included_taxable !== void 0 ? _included_taxable : false;
        })) !== null && _ref !== void 0 ? _ref : []
    });
}
const lowerTaxedBound = (param)=>{
    let { min } = param;
    return min == null || min.amountExcludingTax === 0 ? null : min;
};
// WHY: the read keeps a service's price pre-tax and carries the inclusive figure beside it, so an agreement displaying tax-inclusive prices has to render that figure rather than the price (ANCR-4226 D110)
export function servicePriceIncludingTax(service, displayMode) {
    var _service_taxStatus;
    if (displayMode !== 'including' || ((_service_taxStatus = service.taxStatus) === null || _service_taxStatus === void 0 ? void 0 : _service_taxStatus.type) !== 'taxedAmount') return null;
    return new Decimal(service.taxStatus.taxedAmount.amountIncludingTax);
}
export function serviceRangeIncludingTax(service, displayMode) {
    var _service_taxStatus;
    if (displayMode !== 'including' || ((_service_taxStatus = service.taxStatus) === null || _service_taxStatus === void 0 ? void 0 : _service_taxStatus.type) !== 'taxedRange') return null;
    const { taxedRange } = service.taxStatus;
    const min = lowerTaxedBound(taxedRange);
    return {
        min: min == null ? null : new Decimal(min.amountIncludingTax),
        max: new Decimal(taxedRange.max.amountIncludingTax)
    };
}
export function serviceTaxFigure(service) {
    var _service_taxStatus;
    switch((_service_taxStatus = service.taxStatus) === null || _service_taxStatus === void 0 ? void 0 : _service_taxStatus.type){
        case 'taxedAmount':
            return {
                type: 'amount',
                amount: String(service.taxStatus.taxedAmount.taxAmount)
            };
        case 'taxedRange':
            {
                const { taxedRange } = service.taxStatus;
                const min = lowerTaxedBound(taxedRange);
                return {
                    type: 'range',
                    min: min == null ? null : String(min.taxAmount),
                    max: String(taxedRange.max.taxAmount)
                };
            }
        default:
            return {
                type: 'amount',
                amount: '0'
            };
    }
}
// WHY: mirrors the server's isUsable rule (D107) — a stale mapping (derived-unmapped) never prefills the dropdown.
const usableIntegratedId = (integrations, integration)=>{
    var _mapping_value_integratedId;
    const mapping = integrationMappingFor(integrations, integration);
    if (mapping == null || mapping.value.status !== 'mapped') return null;
    return (_mapping_value_integratedId = mapping.value.integratedId) !== null && _mapping_value_integratedId !== void 0 ? _mapping_value_integratedId : null;
};
export const ratesMatch = (unitRate, taxRateRate)=>{
    try {
        return new Decimal(unitRate).equals(new Decimal(taxRateRate));
    } catch (unused) {
        return false;
    }
};
// WHY: QBO maps each pool component (deduped across rates); Xero maps the whole rate at its combined percentage.
export const mappableUnitsOf = (config, integration)=>{
    if (integration === 'xero') {
        return config.presets.map((preset)=>({
                id: preset.id,
                name: preset.name,
                rate: preset.combinedRate,
                integratedId: usableIntegratedId(preset.integrations, integration)
            }));
    }
    const seen = new Set();
    return config.presets.flatMap((preset)=>preset.components).filter((component)=>{
        if (seen.has(component.id)) return false;
        seen.add(component.id);
        return true;
    }).map((component)=>({
            id: component.id,
            name: component.name,
            rate: component.rate,
            integratedId: usableIntegratedId(component.integrations, integration)
        }));
};
export const countMappedUnits = (units)=>units.filter((unit)=>unit.integratedId != null).length;
export const deriveMapTaxRatesState = (taxRates)=>taxRates.length === 0 ? 'noRates' : 'ratesExist';
